import { useSearchParams } from "next/navigation";

import { FEATURE_FLAGS } from "@/lib/feature-flags/flags";

import { useAuth } from "@/dashboard/contexts/auth-context";
import { useFeatureFlags } from "@/dashboard/contexts/feature-context";
import { HUB_TAB_QUERY_PARAM } from "@/dashboard/lib/hub/tab";
import { HubTab } from "@/dashboard/types/hub";

/**
 * Returns the current hub status from the search params.
 * If the status is not valid, returns FLAGGED (or the allowed transferred tab for call takers).
 * Call takers are clamped to their feature-flag-gated tab:
 *  - Flag OFF → TRANSFERRED_INTERNAL
 *  - Flag ON  → TRANSFERRED
 * Should only be used in components rendered under /hub.
 */
export function useCurrentHubTab() {
  const searchParams = useSearchParams();
  const { user } = useAuth();
  const { hasFeature } = useFeatureFlags();
  const tabParam = searchParams?.get(HUB_TAB_QUERY_PARAM);

  if (user?.neticRole === "call_taker") {
    const useRecentTransfers = hasFeature(FEATURE_FLAGS.NETIC_CALL_TAKER_RECENT_TRANSFERS);
    const defaultTab = useRecentTransfers ? HubTab.TRANSFERRED : HubTab.TRANSFERRED_INTERNAL;
    const allowedTabs = new Set([defaultTab, HubTab.ALL, HubTab.POTENTIAL_CALLBACK]);
    return tabParam && allowedTabs.has(tabParam as HubTab) ? (tabParam as HubTab) : defaultTab;
  }

  const tab =
    tabParam && Object.values(HubTab).includes(tabParam as HubTab)
      ? (tabParam as HubTab)
      : HubTab.FLAGGED;

  return tab;
}
